import { View } from 'web-mojo';

/**
 * ScheduleRouteDrawer — create a scheduled route from a template.
 * Emits 'scheduled' with the new route object when successful.
 *
 * Usage (from template library — template pre-selected):
 *   const drawer = new ScheduleRouteDrawer({ template });
 *   dialog = Dialog.showDialog({ title: `Schedule — ${template.get('name')}`, body: drawer });
 *
 * Usage (from scheduled routes page — no template pre-selected):
 *   const drawer = new ScheduleRouteDrawer({});
 *   dialog = Dialog.showDialog({ title: 'Schedule Route', body: drawer });
 *
 *   drawer.on('scheduled', route => { dialog.hide(); });
 */
export default class ScheduleRouteDrawer extends View {
    constructor({ template = null } = {}) {
        super();
        this.template_model = template;  // null = show template dropdown
        this.templates = [];
        this.operators = [];
        this.warnings = [];
        this.scheduling = false;
        this.stopsWithMissingCoords = 0;
    }

    async onInit() {
        const fetches = [
            app.api.get('/api/routes/operator', { group: app.activeGroup.id, status: 'active', size: 200, sort: 'display_name' }),
        ];
        if (!this.template_model) {
            fetches.push(app.api.get('/api/routes/template', { group: app.activeGroup.id, status: 'published', size: 200, sort: 'name' }));
        }

        const [opResponse, tmplResponse] = await Promise.all(fetches);

        if (opResponse.success && opResponse.data.status) {
            this.operators = opResponse.data.data || [];
        }
        if (tmplResponse?.success && tmplResponse.data.status) {
            this.templates = tmplResponse.data.data || [];
        }

        // Pre-select default operator if template has one
        if (this.template_model) {
            const defaultOp = this.template_model.get('default_operator');
            if (defaultOp) this.defaultOperatorId = defaultOp.id;
            await this._checkStopCoords(this.template_model.get('id'));
        }
    }

    async _checkStopCoords(templateId) {
        if (!templateId) return;
        const resp = await app.api.get('/api/routes/template_stop', {
            template: templateId,
            sort: 'sequence_position',
            size: 200,
        });
        if (resp.success && resp.data.status) {
            const stops = resp.data.data || [];
            this.stopsWithMissingCoords = stops.filter(s =>
                !s.location?.latitude || !s.location?.longitude
            ).length;
        }
    }

    async onActionTemplateChange(event, el) {
        const templateId = el.value;
        this.stopsWithMissingCoords = 0;
        if (templateId) {
            await this._checkStopCoords(templateId);
        }
        const container = this.element?.querySelector('[data-container="coord-warning"]');
        if (container) {
            container.innerHTML = this._coordWarningHtml();
        }
    }

    _coordWarningHtml() {
        if (this.stopsWithMissingCoords === 0) return '';
        const n = this.stopsWithMissingCoords;
        return `
            <div class="alert alert-warning small py-2 mb-3">
                <i class="bi bi-exclamation-triangle me-1"></i>
                ${n} stop${n !== 1 ? 's' : ''} ${n !== 1 ? 'have' : 'has'} no coordinates — ${n !== 1 ? 'they' : 'it'} will not appear on the operator's map.
                Edit those locations to add coordinates before scheduling.
            </div>
        `;
    }

    async onActionSchedule() {
        if (this.scheduling) return false;

        const dateEl = this.element.querySelector('[name=scheduled_date]');
        const operatorEl = this.element.querySelector('[name=operator]');
        const templateEl = this.element.querySelector('[name=template]');

        const scheduledDate = dateEl?.value;
        const operatorId = operatorEl?.value;
        const templateId = this.template_model
            ? this.template_model.get('id')
            : templateEl?.value;

        if (!templateId) {
            app.toast.warning('Template is required');
            return false;
        }
        if (!scheduledDate) {
            app.toast.warning('Route date is required');
            return false;
        }
        if (!operatorId) {
            app.toast.warning('Operator is required');
            return false;
        }

        this.scheduling = true;
        this.warnings = [];

        // Step 1: Create draft route
        const createResponse = await app.api.post('/api/routes/route', {
            template: parseInt(templateId),
            operator: parseInt(operatorId),
            scheduled_date: scheduledDate,
            group: app.activeGroup.id,
        });

        if (!createResponse.success || !createResponse.data.status) {
            this.scheduling = false;
            app.toast.error(createResponse.data?.error || 'Failed to create route');
            return false;
        }

        const route = createResponse.data.data;

        // Step 2: Schedule the route (materialises stops, provisions access windows)
        const scheduleResponse = await app.api.patch(`/api/routes/route/${route.id}`, {
            action: 'schedule',
        });

        this.scheduling = false;

        if (!scheduleResponse.success || !scheduleResponse.data.status) {
            app.toast.error(scheduleResponse.data?.error || 'Failed to schedule route');
            return false;
        }

        const scheduled = scheduleResponse.data.data || scheduleResponse.data;
        this.warnings = scheduled?.warnings || [];
        this.emit('scheduled', scheduled);

        if (this.warnings.length > 0) {
            // Show warnings in the form body, then close after a moment
            this.render();
            return new Promise(resolve => setTimeout(() => resolve(true), 1500));
        }
        return true;
    }

    _routeSummaryHtml() {
        const t = this.template_model;
        if (!t) return '';
        const stopCount = t.get('stop_count') || 0;
        const durMins = t.get('total_duration_seconds') ? Math.round(t.get('total_duration_seconds') / 60) : null;
        const distKm = t.get('total_distance_meters') ? (t.get('total_distance_meters') / 1000).toFixed(1) : null;

        if (!stopCount && !durMins) return '';

        const parts = [];
        if (stopCount) parts.push(`${stopCount} stop${stopCount !== 1 ? 's' : ''}`);
        if (durMins) {
            const h = Math.floor(durMins / 60);
            const m = durMins % 60;
            parts.push(`Est. ${h > 0 ? `${h}h ` : ''}${m > 0 ? `${m}min` : ''}`);
        }
        if (distKm) parts.push(`${distKm} km`);

        return `
            <div class="p-3 bg-light rounded mb-3 small">
                <div class="fw-semibold mb-1">Route Summary</div>
                <div class="text-muted">${parts.join(' · ')}</div>
                <div class="text-muted mt-1">
                    <i class="bi bi-shield-check text-success me-1"></i>Access windows auto-provisioned
                </div>
            </div>
        `;
    }

    _warningsHtml() {
        if (this.warnings.length === 0) return '';
        const items = this.warnings.map(w => `<li>${w}</li>`).join('');
        return `
            <div class="alert alert-warning small mt-3">
                <i class="bi bi-exclamation-triangle me-1"></i>
                <strong>Warnings</strong>
                <ul class="mb-0 mt-1">${items}</ul>
            </div>
        `;
    }

    set template(v) { this._t = v; }

    get template() {
        const t = this.template_model;
        const defaultOpId = this.defaultOperatorId;

        const operatorOptions = this.operators.map(op => {
            const selected = op.id === defaultOpId ? 'selected' : '';
            return `<option value="${op.id}" ${selected}>${op.display_name || op.id}</option>`;
        }).join('');

        // Default date = tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const defaultDate = tomorrow.toISOString().slice(0, 10);

        const templateSection = t
            ? `<div class="mb-1 text-muted small">${t.get('name')}</div>
               ${t.get('recurrence_pattern') ? `<div class="mb-3 small text-muted">Recurrence: ${t.get('recurrence_pattern')}</div>` : ''}`
            : `<div class="mb-3">
                   <label class="form-label">Template *</label>
                   <select class="form-select" name="template" data-action="template-change">
                       <option value="">Select published template…</option>
                       ${this.templates.map(tmpl =>
                           `<option value="${tmpl.id}">${tmpl.name}${tmpl.route_type ? ` (${tmpl.route_type})` : ''}</option>`
                       ).join('')}
                   </select>
               </div>`;

        return `
            <div class="p-3">
                ${templateSection}

                <div data-container="coord-warning">${this._coordWarningHtml()}</div>

                <div class="mb-3">
                    <label class="form-label">Route Date *</label>
                    <input type="date" class="form-control" name="scheduled_date" value="${defaultDate}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Operator *</label>
                    <select class="form-select" name="operator">
                        <option value="">Select operator…</option>
                        ${operatorOptions}
                    </select>
                </div>

                ${this._routeSummaryHtml()}

                ${this._warningsHtml()}

                <div class="text-center text-muted small mt-2">
                    Operator will receive a push notification.
                </div>
            </div>
        `;
    }
}
